import { useRef, useState } from "react";

import { toast } from "@/utils/notifications";

import { BillingSectionSaveCoordinator } from "../contexts/BillingSectionSaveContext";

type UseSaveBillingInfo = {
  /** True while the billing tab's sections are being written. */
  isSaving: boolean;
  /** Writes what the billing tab's sections hold. Does nothing while a save is already running. */
  saveBillingInfo: () => Promise<void>;
};

/**
 * The billing tab's `Save Billing Info` action, with an in-flight guard of its own.
 *
 * A save writes the biller's new charges and then tags the encounter `services-not-sent`, and the tab
 * keeps `Send to Services` disabled while `isSaving` is true. A send that started during the save
 * would read the new charges, send them, and tag the encounter `services-sent`, and the save's tag,
 * landing after it, would put the encounter back on the Missing Services queue.
 *
 * The ref stops a second click in the same tick, before the state reaches the buttons.
 */
function useSaveBillingInfo(billingSectionSave: BillingSectionSaveCoordinator): UseSaveBillingInfo {
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  // A section that did write has already told the biller how it went, in terms of what it was
  // writing — this only has to speak up when there was nothing to write at all.
  const saveBillingInfo = async () => {
    if (isSavingRef.current) return;

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      if ((await billingSectionSave.saveAll()) !== "nothing-to-write") return;

      toast({
        type: "warning",
        title: "Nothing to save",
        message: "This encounter has no unsaved billing changes.",
        autoClose: 3000,
      });
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  return { isSaving, saveBillingInfo };
}

export default useSaveBillingInfo;
